import { Maybe } from "@figurepos/fgr-lib-business-logic/lib/common/types"
import { getCurrentAgenda } from "../../../router/selectors"
import type { StateObject } from "../../../redux/types"
import {
    getFilters,
    getSelectedLocationIdsFromGlobalFilter,
    getTimezoneForQueryParam,
} from "../../../filters/selectors"
import { GLOBAL_FILTER_IDENTIFIER } from "../../../filters/reducer"
import type { TimesheetRecord, UserAccount, UserAccountLocation, UserAccountRole } from "../../../data/types"
import { getUserAccounts } from "../../../data/selectors"
import { getTimesheetReport } from "../selectors"
import { MainMenuAgendaEnum } from "../../../router/types"
import { getMidnightOffset } from "../../settings/selectors"
import { getUtcEndOfDay, getUtcStartOfDay } from "../../../../utils/dateFunctions"
import { IsoDateTime } from "@figurepos/fgr-lib-business-logic/lib/common/dateTime/types"
import { RoleDTO } from "../../employees/positions/types"

export const getTimesheetsDateRange = (state: StateObject): { from: IsoDateTime; to: IsoDateTime } => {
    const globalFilters = getFilters(state, GLOBAL_FILTER_IDENTIFIER)
    const selectedLocationIds = getSelectedLocationIdsFromGlobalFilter(state)
    const midnightOffset = getMidnightOffset(state)
    const timezone = getTimezoneForQueryParam(state, selectedLocationIds)
    const from = globalFilters.date.from
    const to = globalFilters.date.to
    const startOfDay = getUtcStartOfDay(from, timezone, midnightOffset)
    const endOfDay = getUtcEndOfDay(to, timezone, midnightOffset)
    return {
        from: startOfDay,
        to: endOfDay,
    }
}

export const isTimesheetReportAgendaOpened = (state: StateObject) =>
    getCurrentAgenda(state) === MainMenuAgendaEnum.TimesheetReport

export const getUserAccountRoles = (state: StateObject, userAccountId: string): UserAccountRole[] => {
    const userAccounts = getUserAccounts(state)
    const user = userAccounts.find(ua => ua.id === userAccountId)

    if (!user) {
        return []
    }

    return user.roles
}

export const getUserAccountRolesIds = (state: StateObject, userAccountId: string): string[] =>
    getUserAccountRoles(state, userAccountId).map(r => r.roleId)

export const getUserAccountLocations = (state: StateObject, userAccountId: string): UserAccountLocation[] => {
    const userAccounts = getUserAccounts(state)
    const user = userAccounts.find(ua => ua.id === userAccountId)

    if (!user) {
        return []
    }

    return user.locations
}

export const getLoadingUserAccountIds = (state: StateObject) => getTimesheetReport(state).loadingUserAccountIds

export const getUserAccountsValidForTimesheets = (state: StateObject): UserAccount[] => {
    return getUserAccounts(state).filter(user => user.locations.length > 0 && user.roles.length > 0 && user.isActive)
}

export const computeRoleSalary = (
    roleId: Maybe<string>,
    allRoles: RoleDTO[],
    userRoles: UserAccountRole[],
    r: Maybe<TimesheetRecord>
): number => {
    const role = allRoles.find((roleData: RoleDTO) => roleData.id === roleId)
    const userRole = userRoles.find(ur => ur.roleId === roleId)

    if (r && r.hourlyRate) {
        return r.hourlyRate
    }

    if (userRole && userRole.salaryRate) {
        return userRole.salaryRate
    }

    if (role && role.salaryRate) {
        return role.salaryRate
    }

    return 0
}
